import { ShopifyProduct } from "@/lib/shopify";
import { cleanProductTitle, dedupeProducts } from "@/lib/productTitle";

/**
 * Homepage rails used to render as a clearance bin: Shopify's default ordering
 * clusters by product type, so a "under $250" rail came back as 10 outdoor rugs
 * with duplicate names. This picks a varied display set from a larger pool.
 *
 * - at most `maxPerType` products per productType
 * - no duplicate cleaned display names
 * - deterministic shuffle (stable per day) so renders never flicker
 */

// Small deterministic PRNG (mulberry32) keyed off a string seed.
const hashSeed = (seed: string) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (a: number) => () => {
  a = (a + 0x6d2b79f5) >>> 0;
  let t = a;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** Today's date as a stable seed component (UTC day). */
export const dailySeed = () => new Date().toISOString().slice(0, 10);

const shuffle = <T,>(items: T[], seed: string): T[] => {
  const rnd = mulberry32(hashSeed(seed));
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/**
 * Homepage curation filter — applied to homepage rails ONLY (not category
 * pages, search, or the sitemap; nothing is hidden from the store).
 *
 * The affordable pool contains ~94 Christmas Ornaments, ~86 Doormats and a
 * long tail of dated novelty gifts (measured Aug 2026: 7.5% of 1,500 sampled).
 * Those read as off-brand for a store positioned on warm, timeless minimalism,
 * so the homepage does not showcase them. Edit these two lists to tune it.
 *
 * Deliberately conservative: the supplier taxonomy word "Novelty" is NOT a
 * signal — it appears in ordinary titles like "Brown Ceramic Novelty Bedside
 * Lamp", which are fine.
 */
const SEASONAL_PRODUCT_TYPES = ["Christmas Ornaments"];
const SEASONAL_TITLE_RE = /christmas|holiday|santa|halloween|pumpkin|easter|valentine|thanksgiving|snowman/i;

/**
 * Seasonal detection shared with category ordering. Category pages do NOT hide
 * these — they sort them to the end so out-of-season items never lead a shelf.
 */
export const isSeasonalProduct = (p: ShopifyProduct): boolean => {
  const type = p.node.productType || "";
  const title = p.node.title || "";
  return (
    SEASONAL_PRODUCT_TYPES.includes(type) ||
    SEASONAL_TITLE_RE.test(title) ||
    SEASONAL_TITLE_RE.test(type)
  );
};
const OFF_BRAND_TITLE_RE = /social distancing|covid|bling|diving man|man cave|wine o'clock/i;

/** True when a product should not appear in a homepage rail. */
export const isHomepageCurated = (p: ShopifyProduct): boolean => {
  const type = p.node.productType || "";
  if (SEASONAL_PRODUCT_TYPES.includes(type)) return false;
  const title = p.node.title || "";
  if (SEASONAL_TITLE_RE.test(title) || SEASONAL_TITLE_RE.test(type)) return false;
  if (OFF_BRAND_TITLE_RE.test(title)) return false;
  return true;
};

export interface DiversifyOptions {
  count: number;
  maxPerType?: number;
  seed?: string;
}

export const diversifyRail = (
  pool: ShopifyProduct[],
  { count, maxPerType = 2, seed = "rail" }: DiversifyOptions
): ShopifyProduct[] => {
  const unique = dedupeProducts(
    pool.filter((p) => (p.node.images?.edges?.length || 0) > 0 && isHomepageCurated(p))
  );
  // Sort by handle first so the shuffle input is order-independent — pools are
  // assembled from several concurrent requests that can resolve in any order.
  const stable = [...unique].sort((a, b) => a.node.handle.localeCompare(b.node.handle));
  const shuffled = shuffle(stable, `${seed}-${dailySeed()}`);

  const seenName = new Set<string>();
  const typeCount: Record<string, number> = {};
  const picked: ShopifyProduct[] = [];

  // Compare on the *base* cleaned title, not the uniquified one: two SKUs that
  // clean down to the same name must never both appear in the rail, even when
  // uniqueDisplayNames would later tell them apart with a price label.
  const nameOf = (p: ShopifyProduct) =>
    cleanProductTitle(p.node.title, p.node.productType).trim().toLowerCase();

  // Pass 1 — enforce type variety and name uniqueness.
  for (const p of shuffled) {
    if (picked.length >= count) break;
    const type = p.node.productType || "Other";
    if ((typeCount[type] || 0) >= maxPerType) continue;
    const name = nameOf(p);
    if (seenName.has(name)) continue;
    typeCount[type] = (typeCount[type] || 0) + 1;
    seenName.add(name);
    picked.push(p);
  }

  // Pass 2 — relax the per-type cap only if the pool is too thin to fill the rail.
  if (picked.length < count) {
    const ids = new Set(picked.map((p) => p.node.id));
    for (const p of shuffled) {
      if (picked.length >= count) break;
      if (ids.has(p.node.id)) continue;
      const name = nameOf(p);
      if (seenName.has(name)) continue;
      seenName.add(name);
      picked.push(p);
    }
  }

  return picked;
};

/**
 * Shopify's default/best-selling ordering clusters hard by product type — the
 * affordable pool came back as 78 outdoor rugs out of 80 — so a single query can
 * never yield a varied rail. Fetch a slice per product type instead.
 */
export const fetchTypePool = async (
  types: string[],
  perType: number,
  extraQuery: string,
  sortKey = "BEST_SELLING",
  reverse = false
): Promise<ShopifyProduct[]> => {
  const { fetchProducts } = await import("@/lib/shopify");
  // Storefront API throttles bursts, so cap concurrency instead of firing one
  // request per type at once (that returned empty rails).
  const CONCURRENCY = 3;
  const queue = [...types];
  const out: ShopifyProduct[] = [];
  const worker = async () => {
    for (;;) {
      const t = queue.shift();
      if (!t) return;
      try {
        const page = await fetchProducts(
          perType,
          `product_type:"${t}"${extraQuery ? ` AND ${extraQuery}` : ""}`,
          { sortKey, reverse }
        );
        out.push(...page);
      } catch {
        /* skip this type */
      }
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return out;
};
